import { MotionButton } from '@/components/ui/motion-button';
import { slideInFromRight, staggerContainer, staggerItem, viewportOptions } from '@/lib/animations';
import { Link } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { ArrowRight, Bath, Bed, CheckCircle, Home, MapPin, Sparkles, Square, Target } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

// Import the same image as the hero section
import HeroImage from '@/assets/Gemini_Generated_Image_nqric1nqric1nqri.jpeg';

// Feature item component
const FeatureItem = ({
  icon: Icon,
  title,
  description
}: {
  icon: React.ElementType;
  title: string;
  description: string;
}) => {
  return (
    <motion.div
      className="flex items-start mb-10"
      variants={staggerItem}
    >
      <div className="w-12 h-12 flex items-center justify-center rounded-md bg-brand shadow-lg shadow-brand/20 shrink-0 mr-6">
        <Icon className="h-6 w-6 text-white" />
      </div>
      <div>
        <h3 className="font-semibold text-xl text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 leading-relaxed">{description}</p>
      </div>
    </motion.div>
  );
};

export default function GetStartedSection() {
  const [activeTab, setActiveTab] = useState<'builder' | 'preview'>('builder');
  const [lastManualSwitch, setLastManualSwitch] = useState<number | null>(null);
  const containerRef = useRef(null);

  // Auto-switch between tabs every 8 seconds, but pause for 15 seconds after manual interaction
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      // If user manually switched recently (within 15 seconds), don't auto-switch
      if (lastManualSwitch && now - lastManualSwitch < 15000) {
        return;
      }

      setActiveTab(current => current === 'builder' ? 'preview' : 'builder');
    }, 8000); // 8 seconds

    return () => clearInterval(interval);
  }, [lastManualSwitch]);

  // Handle manual tab switching
  const handleTabSwitch = (tab: 'builder' | 'preview') => {
    setActiveTab(tab);
    setLastManualSwitch(Date.now());
  };

  // Property data for the preview
  const propertyData = {
    address: "742 Evergreen Terrace",
    price: "$742,000",
    bedrooms: 4,
    bathrooms: 2.5,
    sqft: 2400,
    description: "Beautiful family home in a quiet neighborhood. This 4-bedroom, 2.5-bathroom home offers an open-concept layout, modern kitchen with granite counters, and a large backyard for entertaining. Great neighbors, local taverns nearby, and ample counter space — perfect for donut enthusiasts."
  };

  return (
    <div className="bg-transparent py-20 px-6 sm:px-8 lg:px-12">
      <div className="max-w-6xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Left column with header and content */}
          <motion.div
            className="lg:col-span-7 pt-12"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
          >
            <motion.h1
              className="text-4xl sm:text-5xl md:text-6xl font-bold mb-8 text-gray-900 leading-tight"
              variants={staggerItem}
            >
              Ready to be your own agent<span className="text-brand">?</span>
            </motion.h1>

            <motion.p
              className="text-xl text-gray-600 max-w-xl mb-10 leading-relaxed"
              variants={staggerItem}
            >
              Selling your home shouldn't be complicated — or expensive. Swim gives you all the tools of a real estate agent, without the commission.
            </motion.p>

            <motion.div
              className="mb-20"
              variants={staggerItem}
            >
              <MotionButton
                size="lg"
                className="bg-brand hover:bg-brand/90 text-white px-8 py-6 text-lg font-medium rounded-md shadow-xl shadow-brand/20 transition-all duration-300"
                asChild
              >
                <Link to="/dashboard">
                  Start your listing
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </MotionButton>
              <p className="text-sm text-gray-500 mt-4">Free to start. Pay only when you're ready for the MLS.</p>
            </motion.div>

            <motion.div
              className="space-y-4"
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={viewportOptions}
            >
              <FeatureItem
                icon={Target}
                title="Reach Serious Buyers"
                description="MLS, REALTOR.ca Zillow, Redfin—your listing shows up where 89% of buyers are actually looking."
              />

              <FeatureItem
                icon={Sparkles}
                title="Stand Out from the Crowd"
                description="AI-written descriptions and enhanced photos that drive clicks, showings, and offers."
              />

              <FeatureItem
                icon={CheckCircle}
                title="Stress-Free Selling"
                description="Showings, offers, and docs—all managed in one simple, digital platform."
              />
            </motion.div>
          </motion.div>

          {/* Right column with image/visualization */}
          <motion.div
            ref={containerRef}
            className="lg:col-span-5 relative pt-8"
            variants={slideInFromRight}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOptions}
          >
            <div className="bg-linear-to-br from-white to-slate-100 rounded-2xl shadow-2xl overflow-hidden border border-gray-200 h-full min-h-[600px] flex flex-col">
              {/* Tab header */}
              <div className="bg-white border-b border-gray-200 p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500 mr-2"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
                  </div>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => handleTabSwitch('builder')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${activeTab === 'builder'
                          ? 'text-brand bg-brand-lighter/30'
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      Builder
                    </button>
                    <button
                      onClick={() => handleTabSwitch('preview')}
                      className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${activeTab === 'preview'
                          ? 'text-brand bg-brand-lighter/30'
                          : 'text-gray-500 hover:text-gray-700'
                        }`}
                    >
                      Preview
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 grow overflow-y-auto">
                <motion.div
                  key={activeTab}
                  initial={{ x: activeTab === 'preview' ? 100 : -100, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  exit={{ x: activeTab === 'builder' ? -100 : 100, opacity: 0 }}
                  transition={{ duration: 0.5, ease: "easeInOut" }}
                >
                  {activeTab === 'builder' ? (
                    <>
                      {/* Step indicator */}
                      <div className="flex items-center mb-8">
                        <div className="w-8 h-8 rounded-full bg-brand text-white flex items-center justify-center text-sm font-medium">1</div>
                        <div className="h-[2px] w-10 bg-brand-light/40"></div>
                        <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-sm font-medium">2</div>
                        <div className="h-[2px] w-10 bg-gray-200"></div>
                        <div className="w-8 h-8 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-sm font-medium">3</div>
                      </div>

                      <h3 className="text-lg font-semibold mb-6">Property Details</h3>

                      {/* Form fields */}
                      <div className="space-y-6 mb-8">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                          <div className="bg-white border border-gray-300 rounded-md p-3">
                            <span className="text-gray-800">742 Evergreen Terrace</span>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Bedrooms</label>
                            <div className="relative">
                              <select className="appearance-none bg-white border border-gray-300 rounded-md w-full p-3 pr-8 text-gray-800">
                                <option>4</option>
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Bathrooms</label>
                            <div className="relative">
                              <select className="appearance-none bg-white border border-gray-300 rounded-md w-full p-3 pr-8 text-gray-800">
                                <option>2.5</option>
                              </select>
                              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                                <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Listing Price</label>
                          <div className="relative">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                              <span className="text-gray-500">$</span>
                            </div>
                            <input type="text" className="pl-8 bg-white border border-gray-300 rounded-md w-full p-3 text-gray-800" value="742,000" readOnly />
                          </div>
                        </div>
                      </div>

                      {/* Photos section */}
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-lg font-semibold">Property Photos</h3>
                          <div className="text-sm text-brand font-medium flex items-center cursor-pointer">
                            <span>Add Photos</span>
                            <ArrowRight className="ml-1 h-3 w-3" />
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div className="aspect-video bg-linear-to-br from-brand-lighter/30 to-brand-lighter/50 rounded-md flex items-center justify-center">
                            <Home className="h-6 w-6 text-brand-light" />
                          </div>
                          <div className="aspect-video bg-linear-to-br from-brand-lighter/30 to-brand-lighter/50 rounded-md flex items-center justify-center">
                            <div className="w-6 h-6 rounded-full border-2 border-brand-light border-dashed flex items-center justify-center">
                              <span className="text-brand-light font-bold text-xs">+</span>
                            </div>
                          </div>
                          <div className="aspect-video bg-linear-to-br from-brand-lighter/30 to-brand-lighter/50 rounded-md flex items-center justify-center">
                            <div className="w-6 h-6 rounded-full border-2 border-brand-light border-dashed flex items-center justify-center">
                              <span className="text-brand-light font-bold text-xs">+</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Preview View */
                    <div className="space-y-6">
                      {/* Main property image */}
                      <div className="aspect-video bg-linear-to-br from-brand-lighter/40 to-brand-lighter/60 rounded-lg flex items-center justify-center relative overflow-hidden">
                        <img
                          src={HeroImage}
                          alt="Property"
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-3 right-3 bg-black/50 text-white px-2 py-1 rounded text-xs font-medium">
                          1 / 3
                        </div>
                      </div>

                      {/* Property details */}
                      <div>
                        <div className="flex items-start justify-between mb-3">
                          <h2 className="text-2xl font-bold text-gray-900">{propertyData.price}</h2>
                          <div className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                            For Sale
                          </div>
                        </div>

                        <div className="flex items-center text-gray-600 mb-4">
                          <MapPin className="h-4 w-4 mr-1" />
                          <span className="text-sm">{propertyData.address}</span>
                        </div>

                        <div className="flex items-center space-x-4 mb-6">
                          <div className="flex items-center text-gray-700">
                            <Bed className="h-4 w-4 mr-1" />
                            <span className="text-sm font-medium">{propertyData.bedrooms} bed</span>
                          </div>
                          <div className="flex items-center text-gray-700">
                            <Bath className="h-4 w-4 mr-1" />
                            <span className="text-sm font-medium">{propertyData.bathrooms} bath</span>
                          </div>
                          <div className="flex items-center text-gray-700">
                            <Square className="h-4 w-4 mr-1" />
                            <span className="text-sm font-medium">{propertyData.sqft.toLocaleString()} sqft</span>
                          </div>
                        </div>

                        <div>
                          <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                          <p className="text-gray-600 text-sm leading-relaxed">
                            {propertyData.description}
                          </p>
                        </div>
                      </div>

                      {/* Contact section */}
                      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">Contact Seller</p>
                            <p className="text-xs text-gray-500">Listed with Swim</p>
                          </div>
                          <div className="flex space-x-2">
                            <button className="px-3 py-1 bg-brand text-white text-xs font-medium rounded hover:bg-brand/90 transition-colors">
                              Message
                            </button>
                            <button className="px-3 py-1 border border-gray-300 text-gray-700 text-xs font-medium rounded hover:bg-gray-50 transition-colors">
                              Call
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </div>

              <div className="bg-gray-50 p-4 border-t border-gray-200 flex justify-between items-center">
                {activeTab === 'builder' ? (
                  <>
                    <button className="text-gray-500 font-medium">Save draft</button>
                    <MotionButton
                      className="bg-brand text-white px-4 py-2 rounded-md font-medium text-sm hover:bg-brand/90 transition-colors"
                      asChild
                    >
                      <Link to="/dashboard">
                        Continue to Step 2
                      </Link>
                    </MotionButton>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleTabSwitch('builder')}
                      className="text-gray-500 font-medium hover:text-gray-700 transition-colors"
                    >
                      ← Back to Builder
                    </button>
                    <MotionButton
                      className="bg-brand text-white px-4 py-2 rounded-md font-medium text-sm hover:bg-brand/90 transition-colors"
                      asChild
                    >
                      <Link to="/dashboard">
                        Publish Listing
                      </Link>
                    </MotionButton>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
} 